// Record envelope and merge rules shared by the app and the sync server.
//
// Every document lives inside a SyncRecord. Merging is last-writer-wins on
// `updatedAt`, with the record id breaking exact ties deterministically via
// the device id. Deletions are tombstones so they propagate.

import type { CollectionName, Collections } from './types.ts';

export interface SyncRecord<C extends CollectionName = CollectionName> {
  id: string;
  col: C;
  data: Collections[C] | null;
  updatedAt: number;
  deleted?: boolean;
  /** Device that made the change. */
  device?: string;
  /** Server sequence number, assigned when the server accepts a record. */
  seq?: number;
}

/** Does `incoming` win over `existing`? */
export function wins(incoming: Pick<SyncRecord, 'updatedAt' | 'device'>, existing?: Pick<SyncRecord, 'updatedAt' | 'device'>): boolean {
  if (!existing) return true;
  if (incoming.updatedAt !== existing.updatedAt) return incoming.updatedAt > existing.updatedAt;
  return (incoming.device ?? '') > (existing.device ?? '');
}

export function recordKey(col: CollectionName, id: string): string {
  return `${col}/${id}`;
}

export interface PullResponse {
  records: SyncRecord[];
  seq: number;
  more: boolean;
  /** Identity of the server database; changes if it is replaced/restored. */
  epoch?: string;
}

export interface PushResponse {
  seq: number;
  accepted: number;
  /** Changes the server refused (sitter links): its current versions, to roll back to. */
  restore?: SyncRecord[];
  /** Refused records the server doesn't have at all: remove them locally. */
  drop?: { col: CollectionName; id: string }[];
}
